import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useQuery} from 'react-query'
import {useSolv, useTenant} from "./components/SolvProvider"
import {useIntlEx, useLangOpts} from "./components/IntlUtils.js"
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  BsCol,
  BsRow,
  DropdownMenu,
  DropdownMenuItem,
  FormBody,
  FormGroup,
  FormGroups,
  FormHeader,
  MainContainer,
  useAlert
} from "./components/FormComps.js"
import {openDialog, openDialogCurried} from "./components/DialogUtils.js"
import {AccountTabs, mkHref} from "./components/AccountUtils";
import {useAccessLevelOpts, UserStatus, useThemeOpts} from "./components/UserUtils";
import {PlaceholderImage} from "./components/ImageUtils";
import MemberDeleteDialog from "./MemberDeleteDialog";
import {shortIdEqual} from "./components/StringUtils";
import getLogger from "./components/Logging.js"
import './App.css'
import MemberMoveDialog from "./MemberMoveDialog";
import ReinviteDialog from "./ReinviteDialog";
import MemberMakeOwnerDialog from "./MemberMakeOwnerDialog";

const log = getLogger("Member")

export default function Member(props) {

  const {info, api, session, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const params = useParams()

  const [alert, setAlert] = useAlert()

  const {tenant} = useTenant(params.tenantId)

  const [userId] = useState(!params.userId || "me" === params.userId ? session.user.userId : params.userId)

  const {findAccessLevelOpt} = useAccessLevelOpts()
  const {findLangOpt} = useLangOpts()
  const {findThemeOpt} = useThemeOpts()

  async function getMember(tenantId, userId) {
    const res = await api.getUser(tenantId, userId)
    if (res && res.data) {
      if (res.error) {
        handleError(res.error)
      }
      else {
        // log.debug(">>>Z: member=", res.data)
        return res.data
      }
    }
    else {
      handleError(1)
    }
  }

  const {data: member, isLoading: memberLoading} = useQuery({
    queryFn: () => getMember(tenant.tenantId, userId),
    queryKey: ["member", userId],
    enabled: !!tenant && !!userId
  })

  useEffect(() => {
    if (memberLoading) {
      setBusy(intl.msg("loading"))
    }
    else {
      setBusy(null)
    }
  }, [memberLoading])

  function handleEditClick(e) {
    if (canEdit()) {
      window.location = mkHref({suffix: `/members/${userId}/_edit`, tenant: tenant, user: session?.user})
    }
  }

  function handleError(error) {
    if (error && error.code) {
      // // log.debug(">>>Z: handleError", error.code)
      const code = error.code
      const details = error.details
      switch (code) {
        case "DUPLICATE_USER":
        case "DUPLICATE_KEY":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_user"), field: "inp_emailAddresses", constraint: "valid"})
          break
        case "BANNED_INVITER":
          setAlert({type: "error", text: intl.msg("error_banned_user", details)})
          break
        case "UNKNOWN_INVITER":
        case "INVALID_INVITER":
          setAlert({type: "error", text: intl.msg("invite_error_invalid_inviter", details), field: "inp_emailAddress", constraint: "valid"})
          break
        case "DUPLICATE_INVITEE_USER":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_user", details), field: "inp_emailAddress", constraint: "unique"})
          break
        case "DUPLICATE_INVITEE_TENANT":
          setAlert({type: "error", text: intl.msg("invite_error_duplicate_tenant", details), field: "inp_tenantName", constraint: "unique"})
          break
        case "BANNED_EMAIL_ADDRESS":
          setAlert({type: "error", text: intl.msg("error_banned_email_address", details), field: "inp_emailAddress", constraint: "valid"})
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed", details)})
          break
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function canEdit() {
    if (isInvitation()) {
      return (tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*") || shortIdEqual(member?.createdById, session?.user?.userId))
    }
    if (isMe()) {
      return true
    }
    if (member) {
      return (tenant.accessingAt(member?.userLevel))
    }
    else {
      return false
    }
  }

  function canDelete() {
    if (isMe()) {
      return false
    }
    if (isOwner()) {
      return false
    }
    if (tenant.accessingAs("MANAGER/INSTREAM/*")) {
      return (session?.user?.userLevel > member?.userLevel)
    }
    else {
      return (tenant.accessingAs(["MANAGER/DOWNSTREAM/*"]))
    }
  }

  function isInviter() {
    return isInvitation() && shortIdEqual(session?.user?.userId, session?.user?.createdById)
  }

  // function canMove() {
  //   return (!isOwner() && tenant.accessingAs("ADMINISTRATOR/INSTREAM,DOWNSTREAM/RESELLER,SYSTEM"))
  // }

  function canReinvite() {
    return (isInvitation() && !isOwner() && (tenant.accessingAs("ADMINISTRATOR/*/*") || isInviter()))
  }

  function canMakeOwner() {
    return (!isInvitation() && !isOwner() && tenant.accessingAs(["OWNER/INSTREAM/*", "OPERATOR/DOWNSTREAM/*"]))
  }

  function isInvitation() {
    return (member?.status === "INVITING")
  }

  function isMe() {
    return session?.user?.userId.replace(/\-/g, "") === member?.userId?.replace(/\-/g, "")
  }

  function isOwner() {
    return (member?.userLevel === 100)
  }

  function handleDeleteClick() {
    openDialog("dlg_member_delete")
  }

  function handleMoveClick() {
    openDialog("dlg_member_move")
  }

  function handleMakeOwnerClick() {
    openDialog("dlg_member_make_owner")
  }

  function MoreMenu(props) {

    const comps = []

    if (canReinvite()) {
      comps.push(
        <DropdownMenuItem name="reinvite" onClick={openDialogCurried(`dlg_reinvite`)}/>
      )
    }

    if (canMakeOwner()) {
      comps.push(
        <DropdownMenuItem msg="make_owner" icon="owner" onClick={handleMakeOwnerClick} withDivider={comps.length > 0}/>
      )
    }

    if (canDelete()) {
      comps.push(
        <DropdownMenuItem name="delete" onClick={handleDeleteClick} withDivider={comps.length > 0}/>
      )
    }

    // if (canMove()) {
    //   comps.push(
    //     <DropdownMenuItem name="move" onClick={handleMoveClick} withDivider={comps.length > 0}/>
    //   )
    // }

    return (
      <>
        {
          comps.length > 0 &&
            <DropdownMenu>
              {comps}
            </DropdownMenu>
        }
      </>
    )
  }

  function FormControls(props) {
    const cmps = []
    if (canEdit()) {
      cmps.push(
        <ActionButton name="edit" onClick={handleEditClick}/>
      )
    }
    cmps.push(
      <MoreMenu/>
    )
    return cmps
  }

  return (
    <>
      {
        tenant &&
          <>

            <MainContainer tenant={tenant} menu={MainMenu}>

              <FormHeader>
                <AccountTabs tenant={tenant}/>
                <FormHeader.Toolbar>
                  <FormHeader.Toolbar.Title>
                    <div>{intl.msg("member")}</div>
                    <div className="text-muted">-</div>
                    <div>{member?.displayName}</div>
                    <UserStatus row={member} size="lg" showYou={true}/>
                  </FormHeader.Toolbar.Title>
                  <FormHeader.Toolbar.Controls>
                    <FormControls/>
                  </FormHeader.Toolbar.Controls>
                </FormHeader.Toolbar>
                <FormHeader.Alert alert={alert}/>
              </FormHeader>

              <FormBody>

                <BsRow>

                  <BsCol breakpoint="sm">

                    <BsRow>

                      <BsCol cols="12">

                        <FormGroups>

                          <FormGroup>
                            <FormGroup.Label text={intl.msg(`member_name`)} compact/>
                            <FormGroup.Controls>
                              {member?.fullName}
                            </FormGroup.Controls>
                          </FormGroup>

                          <FormGroup>
                            <FormGroup.Label text={intl.msg(`email_address`)} compact/>
                            <FormGroup.Controls>
                              {member?.emailAddress}
                            </FormGroup.Controls>
                          </FormGroup>

                          <FormGroup>
                            <FormGroup.Label text={intl.msg(`member_access_level`)} compact/>
                            <FormGroup.Controls>
                              {findAccessLevelOpt(member?.userLevel)?.text}
                            </FormGroup.Controls>
                          </FormGroup>

                        </FormGroups>


                      </BsCol>

                      {
                        !isInvitation() &&

                          <BsCol cols="12">

                            <FormGroups>

                              <FormGroup>
                                <FormGroup.Label text={intl.msg(`member_lang`)} compact/>
                                <FormGroup.Controls>
                                  {findLangOpt(member?.lang)?.label || intl.msg("system")}
                                </FormGroup.Controls>
                              </FormGroup>

                              <FormGroup>
                                <FormGroup.Label text={intl.msg(`member_theme`)} compact/>
                                <FormGroup.Controls>
                                  {
                                    member?.theme?.startsWith("http") ? (
                                      member?.theme
                                    ) : (
                                      findThemeOpt(member?.theme)?.text || intl.msg("system")
                                    )
                                  }
                                </FormGroup.Controls>
                              </FormGroup>

                            </FormGroups>

                          </BsCol>
                      }

                    </BsRow>

                  </BsCol>

                  {
                    !isInvitation() &&

                      <BsCol cols="auto" breakpoint="sm" className="pl-sm-0">

                        <FormGroup>
                          <FormGroup.Controls>
                            {
                              member?.imageUrl ? (
                                <img src={member?.imageUrl} alt={intl.msg("member_avatar")} style={{width: "150px", height: "150px", objectFit: "cover"}}/>
                              ) : (
                                <PlaceholderImage icon="person" width="150" height="150"/>
                              )
                            }
                          </FormGroup.Controls>
                        </FormGroup>

                      </BsCol>

                  }

                </BsRow>

              </FormBody>

            </MainContainer>

          </>
      }
      {
        tenant && member &&
          <>
            <ReinviteDialog user={member}/>
            <MemberDeleteDialog tenant={tenant} row={member}/>
            <MemberMoveDialog tenant={tenant} row={member}/>
            <MemberMakeOwnerDialog tenant={tenant} row={member}/>
          </>
      }
    </>
  )
}
